import { useEffect } from "react";
import { Link } from "react-router-dom";
import { setPageMeta } from "@/lib/api";
import { Reveal, MaskedLines } from "@/components/Motion";
import { Mark } from "@/components/Logo";

const VALUES = [
  ["SHOW UP", "Consistency starts before the first mile."],
  ["RUN TOGETHER", "Progress is personal, but the journey is shared."],
  ["EARN THE MILE", "We respect the work and do not look for shortcuts."],
  ["LEAVE NO RUNNER BEHIND", "Every pace has a place here."],
  ["GET BETTER", "Learn, adjust, and return stronger."],
  ["BUILD THE COMMUNITY", "Give more energy than you take."],
];

const MOBILE = typeof window !== "undefined" && window.matchMedia("(max-width: 640px)").matches;

export default function About() {
  useEffect(() => {
    setPageMeta(
      "About Ninja Run Club: The Dallas-Born Run Club Directory",
      "Ninja Run Club is a Dallas-born, human-verified national run club directory with free evidence-backed training, weekly runs, and club collabs. Here is who we are and how listings get verified."
    );
  }, []);

  return (
    <div className="bg-night min-h-screen">
      <header className="relative overflow-hidden bg-ink border-b border-white/10">
        <video
          poster="/video/run-12-poster.jpg"
          autoPlay
          muted
          loop
          playsInline
          aria-label="Runner checking a watch before a track rep"
          className="absolute inset-0 h-full w-full object-cover opacity-25"
          data-testid="about-hero-video"
        >
          <source src={MOBILE ? "/video/run-12-mobile.mp4" : "/video/run-12.mp4"} type="video/mp4" />
          <source src="/video/run-12.webm" type="video/webm" />
        </video>
        <div className="relative px-5 lg:px-10 pt-24 pb-20">
          <h1 className="font-mono-label text-white/60">ABOUT NINJA RUN CLUB</h1>
          <MaskedLines
            lines={["MILES", "MAKE US."]}
            lineClassName="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95]"
            className="mt-5"
          />
        </div>
      </header>

      <section className="px-5 lg:px-10 py-20 max-w-3xl" data-testid="about-story">
        <Reveal>
          <p className="text-white/80 text-lg leading-relaxed">
            Ninja Run Club was born in Dallas from a simple belief: running gets better when it has a purpose and a community. The brand connects the discipline of individual training with the energy of people moving together.
          </p>
          <p className="text-white/60 leading-relaxed mt-6 text-sm lg:text-base">
            We started on Dallas pavement, on the Katy Trail before sunrise and around White Rock Lake on Saturday mornings. But the mission was never one city. Ninja Run Club is building a national platform: a directory that helps any runner find their people in any city, training that respects the evidence, events that give you a reason to show up, and collaborations that put local clubs on shirts, on stages, and on the map.
          </p>
          <p className="text-white/60 leading-relaxed mt-6 text-sm lg:text-base">
            NRC grows from the same roots as NinjAthlete: the Kaizen mindset of continuous improvement, HYROX credibility earned under fatigue, and the conviction that the work is the way. Where NinjAthlete trains the hybrid athlete, Ninja Run Club builds the running community around them.
          </p>
        </Reveal>
      </section>

      <section className="light-section bg-paper text-ink px-5 lg:px-10 py-20" data-testid="about-mission">
        <Reveal>
          <p className="font-mono-label text-ink/40">THE MISSION</p>
          <h2 className="font-display text-3xl lg:text-5xl mt-5 max-w-4xl leading-tight">
            HELP PEOPLE BECOME BETTER RUNNERS AND BUILD STRONGER COMMUNITIES THROUGH SHARED MILES.
          </h2>
          <p className="text-ink/60 text-sm max-w-2xl mt-6">
            Practical training, local run clubs, meaningful events, and collaborations that give every runner a reason to show up.
          </p>
        </Reveal>
      </section>

      <section className="px-5 lg:px-10 py-20 border-b border-white/10" data-testid="about-values">
        <p className="font-mono-label text-white/40 mb-10">SIX VALUES. NO POSTERS.</p>
        {VALUES.map(([title, desc], i) => (
          <Reveal key={title} delay={i * 0.04}>
            <div className="grid grid-cols-12 items-baseline gap-4 border-t border-white/10 py-7 last:border-b">
              <span className="col-span-2 lg:col-span-1 font-mono-label text-white/30">0{i + 1}</span>
              <h3 className="col-span-10 lg:col-span-5 font-display text-2xl lg:text-4xl">{title}</h3>
              <p className="col-span-10 col-start-3 lg:col-span-5 lg:col-start-8 text-white/50 text-sm">{desc}</p>
            </div>
          </Reveal>
        ))}
      </section>

      <section className="px-5 lg:px-10 py-20 border-b border-white/10" data-testid="about-what-we-do">
        <h2 className="font-display text-3xl lg:text-4xl">WHAT WE DO.</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 mt-8">
          {[
            ["THE DIRECTORY", "A national, human-verified directory of run clubs — searchable by city, meet day, and pace culture. Community submitted, never invented.", "/clubs", "FIND A CLUB"],
            ["THE TRAINING", "74+ evidence-backed guides and a free plan builder covering 5K to ultra, HYROX, nutrition, and recovery — each reviewed before it publishes.", "/training", "READ THE GUIDES"],
            ["THE RUNS", "Weekly Dallas runs with pace leaders and a no-runner-left-behind rule: Saturday community runs, Track Tuesday, and sunrise long runs.", "/events", "SEE THE CALENDAR"],
            ["THE COLLABS", "Co-branded drops that put local run clubs on shirts with design, production, and revenue share handled by NRC.", "/collabs", "BUILD A COLLAB"],
          ].map(([title, desc, to, cta]) => (
            <div key={title} className="bg-night p-6 flex flex-col">
              <h3 className="font-mono-label text-white/40">{title}</h3>
              <p className="text-white/60 text-sm mt-3 flex-1">{desc}</p>
              <Link to={to} className="font-mono-label link-underline text-white/60 mt-5 w-fit">{cta} →</Link>
            </div>
          ))}
        </div>
      </section>

      <section className="px-5 lg:px-10 py-20 border-b border-white/10 max-w-3xl" data-testid="about-verification">
        <h2 className="font-display text-3xl lg:text-4xl">HOW LISTINGS GET VERIFIED.</h2>
        <p className="text-white/60 leading-relaxed mt-6 text-sm lg:text-base">
          Three rules govern every club in the directory. First, we never invent a handle, a schedule, or a founder — when we
          cannot confirm a detail from public sources, we say so and point you to the club's own channels. Second, every club can
          claim its profile and correct the record at any time. Third, a human reviews every submission before it publishes;
          nothing is auto-published and nothing is pay-to-play.
        </p>
        <p className="text-white/60 leading-relaxed mt-5 text-sm lg:text-base">
          Verified clubs show a last-checked date and a source on their profile. Ranked clubs are scored on a published rubric —
          pace honesty, no-drop mechanics, and first-timer experience carry the most weight, because those are the things runners
          actually complain about. Spot an error? Every profile has a "suggest a correction" link, and a person reads every one.
        </p>
      </section>

      <section className="px-5 lg:px-10 py-24 text-center" data-testid="about-cta">
        <Mark white className="h-12 w-14 mx-auto" />
        <p className="font-display text-3xl lg:text-5xl mt-8">DALLAS BORN. BUILT TO RUN EVERYWHERE.</p>
        <div className="flex flex-wrap justify-center gap-4 mt-10">
          <Link to="/clubs" data-testid="about-find-club-cta" className="btn-invert bg-white text-ink font-mono-label px-8 py-4 border border-white hover:bg-transparent hover:text-white">
            FIND A RUN
          </Link>
          <Link to="/join" data-testid="about-join-cta" className="btn-invert border border-white/40 font-mono-label px-8 py-4 hover:bg-white hover:text-ink">
            JOIN THE CLUB
          </Link>
        </div>
      </section>
    </div>
  );
}
